<template>
  <component
    :is="tag"
    :type="tag === 'button' ? type : undefined"
    :to="tag === 'router-link' ? to : undefined"
    :href="tag === 'a' ? href : undefined"
    :disabled="disabled || loading"
    :class="buttonClasses"
    v-bind="$attrs"
    @click="tag === 'button' ? handleClick : undefined"
  >
    <span v-if="loading" class="absolute inset-0 flex items-center justify-center">
      <Loader2 class="h-4 w-4 animate-spin" />
    </span>

    <span :class="{ 'opacity-0': loading }" class="flex items-center justify-center">
      <slot name="icon-left" />
      <span
        v-if="$slots.default"
        :class="{ 'ml-2': $slots['icon-left'], 'mr-2': $slots['icon-right'] }"
      >
        <slot />
      </span>
      <slot name="icon-right" />
    </span>
  </component>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import { Loader2 } from 'lucide-vue-next'

export interface BaseButtonProps {
  variant?: 'primary' | 'secondary' | 'danger' | 'success' | 'warning' | 'ghost' | 'outline'
  size?: 'xs' | 'sm' | 'md' | 'lg' | 'xl'
  type?: 'button' | 'submit' | 'reset'
  disabled?: boolean
  loading?: boolean
  block?: boolean
  rounded?: boolean
  to?: string
  href?: string
}

const props = withDefaults(defineProps<BaseButtonProps>(), {
  variant: 'primary',
  size: 'md',
  type: 'button',
})

const emit = defineEmits<{
  click: [event: MouseEvent]
}>()

const tag = computed(() => {
  if (props.to) return 'router-link'
  if (props.href) return 'a'
  return 'button'
})

const buttonClasses = computed(() => {
  const baseClasses = [
    'relative inline-flex items-center justify-center font-medium transition-all duration-200',
    'focus:outline-none focus:ring-2 focus:ring-offset-2',
    'disabled:opacity-50 disabled:cursor-not-allowed',
  ]

  // Enhanced size variants with better spacing
  const sizeClasses = {
    xs: 'px-2.5 py-1.5 text-xs gap-1.5',
    sm: 'px-3 py-2 text-sm gap-2',
    md: 'px-4 py-2.5 text-sm gap-2',
    lg: 'px-5 py-3 text-base gap-2.5',
    xl: 'px-6 py-3.5 text-base gap-3',
  }

  // Variant styles
  const variantClasses = {
    primary: [
      'bg-primary-600 text-white border border-transparent shadow-sm',
      'hover:bg-primary-700 hover:shadow-md focus:ring-primary-500',
      'disabled:hover:bg-primary-600 disabled:hover:shadow-sm',
    ],
    secondary: [
      'bg-white text-gray-700 border border-gray-300 shadow-sm',
      'hover:bg-gray-50 hover:border-gray-400 hover:shadow-md focus:ring-gray-500',
      'disabled:hover:bg-white disabled:hover:border-gray-300 disabled:hover:shadow-sm',
    ],
    danger: [
      'bg-red-600 text-white border border-transparent',
      'hover:bg-red-700 focus:ring-red-500',
      'disabled:hover:bg-red-600',
    ],
    success: [
      'bg-green-600 text-white border border-transparent',
      'hover:bg-green-700 focus:ring-green-500',
      'disabled:hover:bg-green-600',
    ],
    warning: [
      'bg-yellow-600 text-white border border-transparent',
      'hover:bg-yellow-700 focus:ring-yellow-500',
      'disabled:hover:bg-yellow-600',
    ],
    ghost: [
      'bg-transparent text-gray-700 border border-transparent',
      'hover:bg-gray-100 focus:ring-gray-500',
    ],
    outline: [
      'bg-white text-gray-700 border border-gray-300',
      'hover:bg-gray-50 focus:ring-gray-500',
    ],
  }

  // Rounded corners
  const roundedClasses = props.rounded ? 'rounded-full' : 'rounded-md'

  // Block width
  const widthClasses = props.block ? 'w-full' : ''

  return [
    ...baseClasses,
    sizeClasses[props.size],
    ...variantClasses[props.variant],
    roundedClasses,
    widthClasses,
  ]
    .filter(Boolean)
    .join(' ')
})

const handleClick = (event: MouseEvent) => {
  if (!props.disabled && !props.loading) {
    emit('click', event)
  }
}
</script>
